import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import type { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Scene } from "@babylonjs/core/scene";

// Effet de bord requis : `createInstance` n'existe qu'une fois `InstancedMesh`
// importé, et Babylon lève une erreur explicite si on l'oublie.
import "@babylonjs/core/Meshes/instancedMesh";

import { valueNoise2D } from "./noise";
import type { Terrain } from "./terrain";

/** Un arbre planté : ce que le jeu vise, heurte et abat. */
export interface Tree {
  /** Pied du tronc, dans le monde. */
  readonly position: Vector3;
  /** Rayon du tronc à sa base, mise à l'échelle comprise. */
  readonly radius: number;
}

export interface TreeFieldOptions {
  /** Graine du semis. Indépendante de celle du relief. */
  readonly seed: number;
  /** Côté d'une cellule de semis, en unités monde : un arbre au plus par cellule. */
  readonly cellSize: number;
  /** Rayon planté autour du joueur, en cellules. */
  readonly viewRadius: number;
  /** Proportion de cellules portant un arbre, dans `[0, 1]`. */
  readonly density: number;
  /** Rayon dégagé autour de l'origine : le joueur n'apparaît pas dans un tronc. */
  readonly clearingRadius: number;
  /** Rayon du tronc, en unités monde. */
  readonly trunkRadius: number;
  /** Hauteur du tronc, en unités monde. */
  readonly trunkHeight: number;
  /** Rayon du feuillage à sa base. */
  readonly crownRadius: number;
  /** Hauteur du cône de feuillage. */
  readonly crownHeight: number;
}

export const DEFAULT_TREE_FIELD_OPTIONS: TreeFieldOptions = {
  seed: 20250815,
  // 16 unités : à densité 0,38 cela laisse en moyenne 26 unités entre deux
  // troncs — de quoi circuler entre les arbres sans que le semis paraisse
  // aligné sur une grille, ce qu'une maille plus serrée que la portée du regard
  // rendrait visible.
  cellSize: 16,
  // 10 cellules, soit 160 unités : la distance à laquelle le brouillard ne
  // laisse plus passer que 13 % d'un objet. Un arbre qui apparaît là ne se
  // remarque pas. Aller plus loin multiplierait le nombre d'instances à évaluer
  // à chaque image pour des arbres que le brouillard aurait déjà effacés.
  viewRadius: 10,
  density: 0.38,
  clearingRadius: 12,
  trunkRadius: 0.35,
  trunkHeight: 4.2,
  crownRadius: 1.7,
  crownHeight: 4.5,
};

/** Décalages de graine : chaque tirage d'une cellule doit être indépendant. */
const SEED_PRESENCE = 0;
const SEED_JITTER_X = 7919;
const SEED_JITTER_Z = 15731;
const SEED_SCALE = 26417;
const SEED_YAW = 32749;

/** Amplitude de la variation d'échelle, autour de 1. */
const SCALE_VARIATION = 0.25;

interface PlantedTree extends Tree {
  readonly trunk: InstancedMesh;
  readonly crown: InstancedMesh;
  /** Cellule dont l'arbre provient : c'est elle qui décide de son recyclage. */
  cellX: number;
  cellZ: number;
  position: Vector3;
  radius: number;
}

function cellKey(cx: number, cz: number): string {
  return `${cx}:${cz}`;
}

/**
 * Tirage déterministe dans `[0, 1[` pour une cellule.
 *
 * `valueNoise2D` échantillonné sur des coordonnées **entières** rend exactement
 * la valeur hachée du coin de cellule, sans interpolation : deux cellules
 * voisines n'ont donc aucune corrélation, là où un bruit lu entre les nœuds
 * ferait apparaître des paquets d'arbres alignés sur la grille du bruit.
 */
function draw(cx: number, cz: number, seed: number): number {
  return (valueNoise2D(cx, cz, seed) + 1) / 2;
}

/**
 * Semis d'arbres autour du joueur, recyclé comme le sol.
 *
 * Deux contraintes se rejoignent ici. Le monde étant infini, les arbres ne
 * peuvent pas être posés une fois pour toutes : ils sont **plantés à la volée**,
 * en fonction pure des coordonnées de leur cellule, et retrouvés à l'identique
 * en revenant sur ses pas. Et comme un tronc de plus ne doit pas coûter un appel
 * de dessin de plus, tous partagent deux maillages sources par le mécanisme
 * d'instances de Babylon : le GPU n'en voit que deux, quel que soit leur nombre.
 *
 * Seul le tronc est solide. Le feuillage n'entre pas dans le système de
 * collision : le joueur passe sous les branches, et le nombre de maillages
 * testés à chaque déplacement est divisé par deux.
 */
export class TreeField {
  readonly options: TreeFieldOptions;

  private readonly scene: Scene;
  private readonly terrain: Terrain;
  private readonly trunkSource: Mesh;
  private readonly crownSource: Mesh;

  private readonly active = new Map<string, PlantedTree>();
  private readonly pool: PlantedTree[] = [];
  /**
   * Cellules dont l'arbre a été abattu.
   *
   * Le semis étant une fonction pure des coordonnées, sans cette mémoire un
   * arbre coupé repousserait dès que sa cellule sortirait du rayon planté puis y
   * reviendrait. L'ensemble ne grandit que d'une entrée par arbre abattu.
   */
  private readonly harvested = new Set<string>();

  private centerX = 0;
  private centerZ = 0;
  private centered = false;
  private created = 0;
  private disposed = false;

  constructor(scene: Scene, terrain: Terrain, options: Partial<TreeFieldOptions> = {}) {
    this.scene = scene;
    this.terrain = terrain;
    this.options = { ...DEFAULT_TREE_FIELD_OPTIONS, ...options };

    this.trunkSource = this.createSource(
      "tree-trunk",
      {
        height: this.options.trunkHeight,
        diameterTop: this.options.trunkRadius * 1.6,
        diameterBottom: this.options.trunkRadius * 2,
        // Six pans : la silhouette d'un tronc se lit à sa largeur, pas à sa
        // rondeur, et chaque face en moins allège aussi le test de collision.
        tessellation: 6,
      },
      new Color3(0.24, 0.17, 0.12),
    );

    this.crownSource = this.createSource(
      "tree-crown",
      {
        height: this.options.crownHeight,
        diameterTop: 0,
        diameterBottom: this.options.crownRadius * 2,
        tessellation: 7,
      },
      new Color3(0.15, 0.29, 0.17),
    );
  }

  /** Arbres affichés à cet instant. */
  get activeCount(): number {
    return this.active.size;
  }

  /** Instances vivantes, pool compris : ce nombre doit rester borné. */
  get instanceCount(): number {
    return (this.active.size + this.pool.length) * 2;
  }

  /** Maillages sources, à déclarer une fois comme projeteurs d'ombre. */
  get sources(): readonly Mesh[] {
    return [this.trunkSource, this.crownSource];
  }

  /**
   * Recentre le semis sur `(x, z)`.
   *
   * Sort immédiatement tant que le joueur n'a pas changé de cellule : appelée à
   * chaque image, la méthode ne travaille qu'une fois tous les `cellSize`
   * parcourus.
   */
  update(x: number, z: number): void {
    if (this.disposed) return;

    const { cellSize, viewRadius } = this.options;
    const cx = Math.floor(x / cellSize);
    const cz = Math.floor(z / cellSize);
    if (this.centered && cx === this.centerX && cz === this.centerZ) return;

    this.centerX = cx;
    this.centerZ = cz;
    this.centered = true;

    for (const [key, tree] of this.active) {
      if (Math.abs(tree.cellX - cx) > viewRadius || Math.abs(tree.cellZ - cz) > viewRadius) {
        this.recycle(key, tree);
      }
    }

    for (let dz = -viewRadius; dz <= viewRadius; dz++) {
      for (let dx = -viewRadius; dx <= viewRadius; dx++) {
        this.ensure(cx + dx, cz + dz);
      }
    }
  }

  /**
   * L'arbre le plus proche devant `(x, z)`, s'il est à portée.
   *
   * La distance est mesurée **à l'écorce** et non à l'axe du tronc : un gros
   * arbre se coupe donc d'aussi près qu'un petit, ce qui correspond à ce que
   * voit le joueur.
   */
  findTarget(x: number, z: number, facing: number, reach: number, halfAngle: number): Tree | null {
    const forwardX = Math.sin(facing);
    const forwardZ = Math.cos(facing);
    const minimumDot = Math.cos(halfAngle);

    let closest: PlantedTree | null = null;
    let closestDistance = Infinity;

    for (const tree of this.active.values()) {
      const dx = tree.position.x - x;
      const dz = tree.position.z - z;
      const axisDistance = Math.hypot(dx, dz);
      const barkDistance = axisDistance - tree.radius;

      if (barkDistance > reach || barkDistance >= closestDistance) continue;
      // Un arbre sur lequel on est déjà collé n'a pas de direction fiable : on le
      // considère alors comme étant devant, faute de mieux.
      if (axisDistance > 1e-6 && (dx * forwardX + dz * forwardZ) / axisDistance < minimumDot) {
        continue;
      }

      closest = tree;
      closestDistance = barkDistance;
    }

    return closest;
  }

  /** Abat un arbre. Sa cellule reste vide, y compris après un aller-retour. */
  fell(tree: Tree): void {
    for (const [key, planted] of this.active) {
      if (planted !== tree) continue;

      this.harvested.add(key);
      this.recycle(key, planted);
      return;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    // Détruire un maillage source détruit ses instances : les pools et index ne
    // sont vidés que pour ne pas garder de références mortes.
    this.trunkSource.dispose(false, true);
    this.crownSource.dispose(false, true);
    this.active.clear();
    this.pool.length = 0;
  }

  /** Plante l'arbre d'une cellule, si elle en porte un. */
  private ensure(cx: number, cz: number): void {
    const key = cellKey(cx, cz);
    if (this.active.has(key) || this.harvested.has(key)) return;

    const { cellSize, density, clearingRadius, seed } = this.options;
    if (draw(cx, cz, seed + SEED_PRESENCE) >= density) return;

    // Jitter dans les 80 % centraux de la cellule : deux arbres de cellules
    // voisines ne peuvent alors pas se toucher, quelle que soit leur échelle.
    const x = (cx + 0.1 + draw(cx, cz, seed + SEED_JITTER_X) * 0.8) * cellSize;
    const z = (cz + 0.1 + draw(cx, cz, seed + SEED_JITTER_Z) * 0.8) * cellSize;
    if (Math.hypot(x, z) < clearingRadius) return;

    const scale = 1 + (draw(cx, cz, seed + SEED_SCALE) * 2 - 1) * SCALE_VARIATION;
    const yaw = draw(cx, cz, seed + SEED_YAW) * Math.PI * 2;
    const tree = this.pool.pop() ?? this.createTree();

    this.plant(tree, cx, cz, x, z, scale, yaw);
    this.active.set(key, tree);
  }

  private createSource(
    name: string,
    shape: { height: number; diameterTop: number; diameterBottom: number; tessellation: number },
    color: Color3,
  ): Mesh {
    const source = CreateCylinder(name, shape, this.scene);

    const material = new StandardMaterial(`${name}-material`, this.scene);
    material.diffuseColor = color;
    material.specularColor = Color3.Black();
    source.material = material;

    // La source n'est jamais dessinée : elle ne sert que de gabarit. Ses
    // instances, elles, restent rendues — c'est le mécanisme même des instances.
    source.isVisible = false;
    source.isPickable = false;
    source.receiveShadows = true;

    return source;
  }

  private createTree(): PlantedTree {
    const index = this.created++;
    const trunk = this.trunkSource.createInstance(`tree-trunk-${index}`);
    const crown = this.crownSource.createInstance(`tree-crown-${index}`);

    // Seul le tronc arrête le joueur ; le feuillage se traverse.
    trunk.checkCollisions = true;
    trunk.isPickable = false;
    crown.checkCollisions = false;
    crown.isPickable = false;

    return {
      trunk,
      crown,
      position: Vector3.Zero(),
      radius: this.options.trunkRadius,
      cellX: 0,
      cellZ: 0,
    };
  }

  private plant(
    tree: PlantedTree,
    cx: number,
    cz: number,
    x: number,
    z: number,
    scale: number,
    yaw: number,
  ): void {
    const { trunkHeight, crownHeight, trunkRadius } = this.options;
    const y = this.terrain.heightAt(x, z);

    tree.position.set(x, y, z);
    tree.radius = trunkRadius * scale;
    tree.cellX = cx;
    tree.cellZ = cz;

    tree.trunk.scaling.setAll(scale);
    tree.trunk.rotation.y = yaw;
    // Les cylindres de Babylon ont leur origine au milieu : le tronc monte d'une
    // demi-hauteur au-dessus du point de contact, le feuillage se greffe au
    // sommet du tronc en le recouvrant d'un tiers.
    tree.trunk.position.set(x, y + (trunkHeight / 2) * scale, z);
    tree.trunk.setEnabled(true);

    tree.crown.scaling.setAll(scale);
    tree.crown.rotation.y = yaw;
    tree.crown.position.set(x, y + (trunkHeight * 0.7 + crownHeight / 2) * scale, z);
    tree.crown.setEnabled(true);
  }

  private recycle(key: string, tree: PlantedTree): void {
    tree.trunk.setEnabled(false);
    tree.crown.setEnabled(false);
    this.active.delete(key);
    this.pool.push(tree);
  }
}
